import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/ui/cn';
import { useBackToClose } from '@/ui/useBackToClose';

/** Keep in step with the duration-200 classes below. */
const TRANSITION_MS = 200;

/**
 * A centred dialog rendered into <body>.
 *
 * Portalled so it is never clipped by a table's `overflow-hidden`, which is
 * exactly where these get opened from. Escape and a backdrop click both close
 * it (and on a phone, so does Back), and focus moves inside on open so keyboard users are not left behind on
 * the page underneath.
 *
 * Open and close are animated: the backdrop fades and the panel rises and
 * scales in. That needs two pieces of state - `mounted` keeps the DOM alive
 * for the exit animation after `open` goes false, and `shown` flips one frame
 * after mount so the browser paints the hidden state first and actually runs
 * the enter transition. `motion-reduce:` turns it all off for people who ask
 * their OS for no motion. Note the exit only plays when the parent keeps
 * rendering <Modal> and toggles `open`; conditionally rendering the whole
 * element unmounts it instantly.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  // "side" docks it to the right edge as a full-height drawer, for panels that
  // hold a lot and should not hide the list they were opened from.
  placement = 'center',
}) {
  const side = placement === 'side';
  const panelRef = useRef(null);
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  useBackToClose(open, onClose);

  // Mount immediately on open; on close, keep the DOM until the exit
  // transition has finished, then let it go.
  useEffect(() => {
    if (open) {
      setMounted(true);
      return undefined;
    }
    setShown(false);
    const timer = setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  // Flip to the visible state only after the closed state has painted -
  // double rAF, because React 18 can batch a single frame into the first
  // paint and the transition would be skipped.
  useEffect(() => {
    if (!mounted || !open) return undefined;
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
    return () => cancelAnimationFrame(raf);
  }, [mounted, open]);

  useEffect(() => {
    if (!open || !mounted) return undefined;

    function onKeyDown(event) {
      if (event.key === 'Escape') onClose?.();
    }
    document.addEventListener('keydown', onKeyDown);

    // The page behind must not scroll under the dialog.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Focus the panel only when nothing inside it has focus yet. An autoFocus
    // field inside the dialog wins the race - React applies it at mount,
    // before this effect - and refocusing the panel here would silently steal
    // the caret from it, leaving keystrokes landing on a <div>.
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) panel.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, mounted, onClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50 flex',
        // On a phone a dialog rises from the bottom like an app's sheet; from
        // md up it is a centred dialog.
        side ? 'items-stretch justify-end' : 'items-end justify-center md:items-center md:p-4',
        'transition-all duration-200 ease-out motion-reduce:transition-none',
        shown ? 'bg-ink/50 backdrop-blur-sm' : 'bg-ink/0 backdrop-blur-0',
      )}
      onMouseDown={(event) => {
        // mousedown, not click: a click that starts inside the panel and ends
        // on the backdrop - a text selection drag - must not close the dialog.
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={cn(
          'w-full overflow-y-auto bg-surface shadow-lift outline-none',
          'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
          side
            ? cn('h-full sm:rounded-l-2xl', shown ? 'translate-x-0' : 'translate-x-full')
            : cn(
                'max-h-[92vh] rounded-t-[28px] pb-[env(safe-area-inset-bottom)] md:max-h-[90vh] md:rounded-2xl md:pb-0',
                shown
                  ? 'translate-y-0 opacity-100 md:scale-100'
                  : 'translate-y-full opacity-100 md:translate-y-3 md:scale-95 md:opacity-0',
              ),
          // A side drawer is the wireframe's min(460px, 100%): full width on a
          // phone, a fixed panel beside the list everywhere else.
          side
            ? 'max-w-[460px]'
            : size === 'sm'
              ? 'max-w-sm'
              : size === 'lg'
                ? 'max-w-2xl'
                : size === 'xl'
                  ? 'max-w-4xl'
                  : 'max-w-lg',
        )}
      >
        {!side && (
          <span
            className="mx-auto mt-2.5 block h-1.5 w-10 rounded-full bg-border md:hidden"
            aria-hidden="true"
          />
        )}
        <header
          className={cn(
            'sticky top-0 z-10 flex items-start gap-3 border-b border-border bg-surface px-5 py-4',
            !side && 'md:static',
          )}
        >
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-base font-semibold text-content">{title}</h2>
            {description && <p className="mt-0.5 text-xs text-content-muted">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1 text-content-muted transition-colors hover:bg-surface-muted hover:text-content"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </header>
        <div className="p-5">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
